import { styled, IconButton, Button, Paper } from "@mui/material";

// --------------------
// 1) Custom Gradient Checkbox (Netflix red accent)
// --------------------
import Checkbox, { checkboxClasses } from "@mui/material/Checkbox";

export const GradientCheckbox = styled(Checkbox)(({ theme }) => ({
  color: "rgba(255,255,255,0.65)",
  [`&.${checkboxClasses.checked}`]: {
    color: "#E50914",
  },
  "&:hover": {
    backgroundColor: "rgba(229,9,20,0.08)",
  },
}));

// --------------------
// 2) Custom Gradient Button (bold Netflix CTA)
// --------------------
export const GradientButton = styled("button")(() => ({
  background: "#E50914",
  color: "#fff",
  font: "var(--semibold-md)",
  textTransform: "none",
  border: "none",
  borderRadius: "12px",
  padding: "var(--spacing-3x) var(--spacing-5x)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  marginTop: "10px",
  boxShadow: "0 12px 28px rgba(229,9,20,0.35)",
  letterSpacing: "0.01em",
  transition: "transform .05s ease, box-shadow .2s ease, background-color .2s ease",
  "&:hover": {
    background: "#b20710",
    boxShadow: "0 16px 32px rgba(229,9,20,0.45)",
  },
  "&:active": {
    transform: "translateY(1px)",
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));

// --------------------
// 3) Custom Input Field & Icons (dark panel inputs)
// --------------------
export const InputField = styled("div")(() => ({
  width: "100%",
  maxWidth: "560px",
  marginBottom: "8px",
  position: "relative",
  display: "flex",
  flexDirection: "column",
}));

export const InputFieldLabel = styled("div")(() => ({
  font: "var(--body-sm)",
  textAlign: "left",
  marginBottom: "8px",
  color: "rgba(255,255,255,0.85)",
}));

export const InputWrapper = styled("div")(() => ({
  position: "relative",
}));

export const InputIcon = styled("div")(() => ({
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "rgba(255,255,255,0.55)",
  pointerEvents: "none",
}));

const baseInput = {
  width: "100%",
  height: "48px",
  font: "var(--body-md)",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.12)",
  background: "#141414",
  color: "#fff",
  outline: "none",
  transition: "border-color .2s ease, box-shadow .2s ease, background-color .2s ease",
  caretColor: "#fff",
  "&:focus": {
    borderColor: "#E50914",
    boxShadow: "0 0 0 3px rgba(229,9,20,0.15)",
    background: "#161616",
  },
  "::placeholder": {
    color: "rgba(255,255,255,0.48)",
    font: "var(--body-sm)",
    opacity: 1,
  },
};

export const Input = styled("input")(() => ({
  ...baseInput,
  padding: "0 14px 0 42px", // space for leading icon
}));

export const InputWithoutIcon = styled("input")(() => ({
  ...baseInput,
  padding: "0 14px",
}));

export const Label = styled("label")(() => ({
  fontSize: "14px",
  fontWeight: 600,
  marginBottom: "6px",
  fontFamily: '"Lexend", sans-serif',
  color: "rgba(255,255,255,0.85)",
  textAlign: "left",
}));

export const PasswordToggle = styled(IconButton)(() => ({
  position: "absolute",
  right: "10px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "rgba(255,255,255,0.55)",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.06)",
  },
}));

// --------------------
// 4) Social Login Button (subtle dark, crisp border)
// --------------------
export const SocialButton = styled(Button)(({ theme }) => ({
  width: "100%",
  height: "50px",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.16)",
  textTransform: "none",
  font: "var(--semibold-md)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  color: "#fff",
  background: "rgba(20,20,20,0.8)",
  boxShadow: "none",
  transition: "background-color .2s ease, box-shadow .2s ease, border-color .2s ease",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.08)",
    borderColor: "rgba(255,255,255,0.24)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
  },
  [theme.breakpoints.up("xs")]: {
    maxWidth: "100px",
  },
  [theme.breakpoints.up("md")]: {
    maxWidth: "152px",
  },
}));

// --------------------
// Sign-in layout containers (cinematic surfaces)
// --------------------
export const SignInMainContainer = styled("div")(() => ({
  width: "100%",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#0b0b0b",
  position: "relative",
  overflow: "hidden",
  // subtle vignette & glow
  "::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(1200px 600px at 10% 10%, rgba(229,9,20,0.12), transparent 60%), radial-gradient(900px 500px at 90% 20%, rgba(255,255,255,0.06), transparent 60%)",
    pointerEvents: "none",
  },
}));

export const SignInPaperContainer = styled(Paper)(() => ({
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  boxSizing: "border-box",
  background: "#141414",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 16,
  padding: 24,
  boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
}));
